import type { ProofStyle } from './issuer.ts';

/**
 * What NetID asks the company's wallet to prove (SPEC.md §8.3), as plain JSON
 * so it needs no SDK: reveal the legal name, the business number (the CVR for
 * a Danish company) and the country, from an identity issued by an approved
 * company identity provider. The challenge is the binding document's SHA-256.
 *
 * Plus the parsing a verifier needs BEFORE it trusts anything: which
 * challenge, issuer and revealed values a proof claims. Those claims count only
 * after `proof-verify.ts` has checked the zero-knowledge proof.
 */
export const COMPANY_ATTRIBUTES = ['legalName', 'businessNumber', 'legalCountry'] as const;
export type CompanyAttribute = (typeof COMPANY_ATTRIBUTES)[number];

/** Global FinReg (Business ID), the identity provider for company identities on mainnet. */
export const GLOBAL_FINREG_IDP = 3;
export const APPROVED_COMPANY_IDPS: Record<'mainnet' | 'testnet', Record<number, string>> = {
  mainnet: { 3: 'Global FinReg' },
  testnet: {},
};

/** V0 Web3 ID statements: `requestVerifiablePresentation(challenge, statements)`. Account credentials from the idp. */
export function v0Statements(idp = GLOBAL_FINREG_IDP) {
  return [
    {
      idQualifier: { type: 'cred' as const, issuers: [idp] },
      statement: COMPANY_ATTRIBUTES.map((attributeTag) => ({ type: 'RevealAttribute' as const, attributeTag })),
    },
  ];
}

/** The account ID proof statement: `requestIdProof(account, statement, challenge)`. */
export function idProofStatement() {
  return COMPANY_ATTRIBUTES.map((attributeTag) => ({ type: 'RevealAttribute' as const, attributeTag }));
}

export type ProofClaims = {
  style: ProofStyle;
  challenge: string | null;
  issuerIdp: number | null;
  network: 'mainnet' | 'testnet' | null;
  /** did:ccd:<net>:cred:<credId> for an account credential. */
  credId: string | null;
  revealed: Record<string, string>;
  validUntil: string | null;
};

const IDP_DID = /^did:ccd:(mainnet|testnet):idp:(\d+)$/;

/** Reads what a proof CLAIMS (unverified). Throws if it is not a proof of the given style. */
export function proofClaims(style: ProofStyle, json: string): ProofClaims {
  const j = JSON.parse(json) as Record<string, unknown>;
  const out: ProofClaims = { style, challenge: null, issuerIdp: null, network: null, credId: null, revealed: {}, validUntil: null };
  const creds = (j.verifiableCredential ?? []) as Array<Record<string, any>>;
  if (style === 'v0-presentation') {
    if (typeof j.presentationContext !== 'string' || !Array.isArray(creds) || creds.length !== 1) throw new Error('not a V0 presentation with one credential');
    out.challenge = j.presentationContext;
    const c = creds[0];
    const m = String(c.issuer ?? '').match(IDP_DID);
    if (m) {
      out.network = m[1] as 'mainnet' | 'testnet';
      out.issuerIdp = Number(m[2]);
    }
    const id = String(c.credentialSubject?.id ?? '').match(/^did:ccd:(mainnet|testnet):cred:([0-9a-f]{96})$/);
    if (id) out.credId = id[2];
    const st = c.credentialSubject?.statement ?? [];
    const pv = c.credentialSubject?.proof?.proofValue ?? [];
    st.forEach((s: { type: string; attributeTag: string }, i: number) => {
      if (s.type === 'RevealAttribute' && pv[i]?.type === 'RevealAttribute') out.revealed[s.attributeTag] = String(pv[i].attribute);
    });
    return out;
  }
  if (style === 'v1-identity') {
    const ctx = j.presentationContext as { given?: Array<{ label: string; context: unknown }> } | undefined;
    if (!ctx || !Array.isArray(creds) || creds.length !== 1) throw new Error('not a V1 presentation with one credential');
    const cs = ctx.given?.find((g) => g.label === 'ContextString');
    out.challenge = typeof cs?.context === 'string' ? cs.context : null;
    const c = creds[0];
    const m = String(c.issuer ?? '').match(IDP_DID);
    if (m) {
      out.network = m[1] as 'mainnet' | 'testnet';
      out.issuerIdp = Number(m[2]);
    }
    out.validUntil = typeof c.validUntil === 'string' ? c.validUntil : null;
    for (const s of c.credentialSubject?.statement ?? []) {
      if (s.type === 'AttributeValue' && typeof s.attributeTag === 'string') out.revealed[s.attributeTag] = String(s.attributeValue);
    }
    return out;
  }
  // id-proof: { credential, proof: { v, value: { proofs: [...] } } } — revealed values sit in the proofs.
  const proofs = ((j.proof as any)?.value?.proofs ?? []) as Array<{ type: string; attribute?: string }>;
  if (typeof j.credential !== 'string') throw new Error('not an ID proof');
  out.credId = j.credential;
  const stmt = idProofStatement();
  proofs.forEach((p, i) => {
    if (p.type === 'RevealAttribute' && stmt[i]) out.revealed[stmt[i].attributeTag] = String(p.attribute);
  });
  return out;
}
